import React, { useState } from 'react';
import styled from 'styled-components';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  Legend
} from 'recharts';

// Custom tooltip to show engagement details
function CustomTooltip({ active, payload, label }) {
  if (active && payload && payload.length) {
    return (
      <TooltipContainer>
        <TooltipLabel>{label}</TooltipLabel>
        {payload.map((entry, index) => (
          <TooltipValue key={index} style={{ color: entry.color }}>
            {entry.dataKey === 'engagement' ? 'Engagement' : entry.dataKey}:{' '}
            {entry.value} points
          </TooltipValue>
        ))}
      </TooltipContainer>
    );
  }
  return null;
}

function UserEngagementTimelineChart({ data, users = [] }) {
  const [selectedUser, setSelectedUser] = useState('all');
  if (!data || data.length === 0) {
    return (
      <EmptyState>
        <EmptyMessage>
          No engagement data available for the selected period
        </EmptyMessage>
      </EmptyState>
    );
  }

  // Generate chart data based on selected user
  const chartData =
    selectedUser === 'all'
      ? data
      : data.map((dayData) => ({
          date: dayData.date,
          engagement: dayData[selectedUser] || 0
        }));

  // Generate lines for chart
  const renderLines = () => {
    if (selectedUser === 'all') {
      // Show all user lines with different colors
      const colors = [
        'var(--blue)',
        '#10B981',
        '#F59E0B',
        '#EF4444',
        '#8B5CF6',
        '#06B6D4',
        '#F97316'
      ];
      return users.map((user, index) => (
        <Line
          key={user.uid}
          type="monotone"
          dataKey={user.name}
          stroke={colors[index % colors.length]}
          strokeWidth={2}
          dot={{ fill: colors[index % colors.length], strokeWidth: 2, r: 5 }}
          activeDot={{
            r: 7,
            stroke: colors[index % colors.length],
            strokeWidth: 2
          }}
        />
      ));
    }
    return (
      <Line
        type="monotone"
        dataKey="engagement"
        stroke="var(--blue)"
        strokeWidth={2}
        dot={{ fill: 'var(--blue)', strokeWidth: 2, r: 6 }}
        activeDot={{ r: 8, stroke: 'var(--blue)', strokeWidth: 2 }}
      />
    );
  };

  return (
    <ChartContainer>
      <ChartHeader>
        <UserSelector
          value={selectedUser}
          onChange={(e) => setSelectedUser(e.target.value)}>
          <option value="all">All Users</option>
          {users.map((user) => (
            <option key={user.uid} value={user.name}>
              {user.name}
            </option>
          ))}
        </UserSelector>
      </ChartHeader>

      <ResponsiveContainer width="100%" height={300}>
        <LineChart
          data={chartData}
          margin={{
            top: 20,
            right: 30,
            left: 20,
            bottom: 20
          }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
          <XAxis
            dataKey="date"
            tick={{ fontSize: 12, fill: 'var(--brown)' }}
            stroke="var(--light-brown)"
          />
          <YAxis
            tick={{ fontSize: 12, fill: 'var(--brown)' }}
            stroke="var(--light-brown)"
            label={{
              value: 'Engagement Score',
              angle: -90,
              position: 'insideLeft',
              style: { textAnchor: 'middle', fill: 'var(--brown)' }
            }}
            domain={[0, 100]}
          />
          <Tooltip content={<CustomTooltip />} />
          {selectedUser === 'all' && users.length > 1 && (
            <Legend
              wrapperStyle={{
                fontSize: '12px',
                fontFamily: 'Inter, sans-serif',
                paddingTop: '10px',
                lineHeight: '1.8'
              }}
            />
          )}
          {renderLines()}
        </LineChart>
      </ResponsiveContainer>
    </ChartContainer>
  );
}

const ChartContainer = styled.div`
  min-height: 300px;
  width: 100%;

  .recharts-tooltip-wrapper {
    z-index: 100;
  }

  .recharts-legend-item {
    margin-right: 20px !important;
  }
`;

const ChartHeader = styled.div`
  align-items: center;
  display: flex;
  justify-content: flex-end;
  margin-bottom: 1rem;
`;

const UserSelector = styled.select`
  background: white;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  color: var(--brown);
  font-family: 'Inter', sans-serif;
  font-size: 0.875rem;
  padding: 0.5rem;

  &:focus {
    border-color: var(--blue);
    outline: none;
  }
`;

const TooltipContainer = styled.div`
  background: rgba(0, 0, 0, 0.9);
  border: none;
  border-radius: 6px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  color: white;
  font-family: 'Inter', sans-serif;
  font-size: 12px;
  line-height: 1.4;
  padding: 0.75rem;
`;

const TooltipLabel = styled.div`
  color: white;
  font-weight: 600;
  margin-bottom: 0.25rem;
`;

const TooltipValue = styled.div`
  margin-bottom: 0.125rem;

  &:last-child {
    margin-bottom: 0;
  }
`;

const EmptyState = styled.div`
  align-items: center;
  color: var(--light-brown);
  display: flex;
  height: 300px;
  justify-content: center;
`;

const EmptyMessage = styled.p`
  font-family: 'Inter', sans-serif;
  margin: 0;
  text-align: center;
`;

export default UserEngagementTimelineChart;
